import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function fmtBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function fmtTs(s) {
  return s ? new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function StatusBadge({ status }) {
  const m = {
    recording:  'bg-rose-50 text-rose-700 border-rose-200',
    finalizing: 'bg-amber-50 text-amber-800 border-amber-200',
    stored:     'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed:     'bg-gray-100 text-gray-700 border-gray-200',
    purged:     'bg-gray-100 text-gray-500 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${m[status] || 'bg-gray-50'}`}>
      {status}
    </span>
  );
}

function PlayerRow({ rec }) {
  const [expanded, setExpanded] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);
  const canPlay = !!rec.stream_url;

  const onPurge = () => {
    if (!rec.purge_url) return;
    if (!confirm(`Permanently delete this recording for ${rec.user_name}? The row will remain as an audit tombstone but the video file will be deleted from storage.`)) return;
    router.post(rec.purge_url, {}, { preserveScroll: true });
  };

  return (
    <>
      <tr className="border-t border-gray-100">
        <td className="px-3 py-2">
          <div className="text-sm font-medium text-gray-900">{rec.user_name}</div>
          <div className="text-xs text-gray-500">{rec.user_email}</div>
        </td>
        <td className="px-3 py-2"><StatusBadge status={rec.status} /></td>
        <td className="px-3 py-2 text-sm tabular-nums text-gray-700">{rec.chunk_count}</td>
        <td className="px-3 py-2 text-sm tabular-nums text-gray-700">{fmtBytes(rec.byte_size)}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{fmtTs(rec.started_at)}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{fmtTs(rec.finalized_at || rec.last_chunk_at)}</td>
        <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
          {canPlay && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-indigo-600 hover:text-indigo-700 cursor-pointer"
            >
              {expanded ? 'Hide' : 'Play'}
            </button>
          )}
          {rec.stream_url && (
            <>
              {' · '}
              <a href={rec.stream_url} className="text-indigo-600 hover:text-indigo-700" download>
                Download
              </a>
            </>
          )}
          {rec.purge_url && (
            <>
              {' · '}
              <button type="button" onClick={onPurge} className="text-rose-600 hover:text-rose-700 cursor-pointer">
                Purge
              </button>
            </>
          )}
        </td>
      </tr>
      {expanded && canPlay && (
        <tr>
          <td colSpan={7} className="px-3 pb-4 bg-gray-50">
            <div className="max-w-xl">
              <video
                key={rec.stream_url}
                src={rec.stream_url}
                controls
                preload="metadata"
                onError={(e) => {
                  const code = e.target?.error?.code;
                  // 1=ABORTED 2=NETWORK 3=DECODE 4=SRC_NOT_SUPPORTED
                  setPlaybackError(
                    code === 4
                      ? 'Playback failed. The signed URL may have expired (5 min TTL) or the browser blocked the redirect. Reload the page to try again.'
                      : 'Playback failed. Try downloading the file to play locally.'
                  );
                }}
                onPlay={() => setPlaybackError(null)}
                className="w-full rounded-lg shadow-sm bg-black"
              />
              {playbackError && (
                <p className="mt-2 text-xs text-rose-600">{playbackError}</p>
              )}
              {rec.failure_reason && (
                <p className="mt-2 text-xs text-rose-600">{rec.failure_reason}</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SuperadminWebinarRecordings({ webinar, recordings = [] }) {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
        <div>
          <Link href={`/superadmin/webinars/${webinar.id}`} className="text-sm text-indigo-600 hover:text-indigo-700">
            ← Back to webinar
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">Attendance recordings</h1>
          <p className="text-sm text-gray-600">{webinar.title}</p>
        </div>

        {recordings.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
            No viewer recordings yet.
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Viewer</th>
                  <th className="px-3 py-2 text-left font-semibold">Status</th>
                  <th className="px-3 py-2 text-left font-semibold">Chunks</th>
                  <th className="px-3 py-2 text-left font-semibold">Size</th>
                  <th className="px-3 py-2 text-left font-semibold">Started</th>
                  <th className="px-3 py-2 text-left font-semibold">Ended</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((r) => <PlayerRow key={r.id} rec={r} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
